import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import classnames from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { CurrencyInput } from 'ui-components/components/currency-input';
import { FlexRow, FlexColumn } from 'ui-components/components/flex';
import { InfoIcon } from 'ui-components/components/info-icon';

import { NumberInput } from 'ui-components/components/number-input';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { ApplyInsuranceCheckbox } from './apply-insurance-checkbox';
import { CreateLabelDisplay } from './components/create-label-display';
import styles from './styles.module.less';
import { emptyArray } from 'ui-components/default-props/empty';
import FormattedCurrencyContainer from '../../../../../components/formatted-currency/container';

import { PhoneVerificationBanner } from '../../../../../components/phone-verification/banner-message';
import { PhoneVerificationContext } from '../../../../../components/phone-verification/context';
import { Providers } from '../../../../../enums/providers';
import { walletSettingsEnum } from '../../../../../enums/wallet-settings';
import { FundsSummary } from '../../../../../financial-services-ui/surcharge/funds-summary';
import { RetrySection } from '../../../../../financial-services-ui/surcharge/retry-section';
import { AddFundsErrorSection } from '../../../../../pages/settings/payment-and-subscription/components/add-funds-modal/error-section';
import { MINIMUM_PURCHASE_AMT } from '../../../../../pages/settings/payment-and-subscription/components/constants/add-funds-constants';
import { AddFundButtons } from '../../../../../pages/settings/payment-and-subscription/components/funding/add-fund-buttons';
import { useAddFundsContext } from '../../../../../pages/settings/payment-and-subscription/context/add-funds-context-provider';
import Global from '../../../../../store/global-selectors';
import { useWalletBalance, useWalletSetting } from '../../../../contexts/wallet-data-context-provider';
import { messages } from '../../../../messages';
import { useWalletRegistrationModalContext } from '../../contexts/wallet-registration-modal-context';
import commonStyles from '../../style.module.css';
import { UpsTermsCheckbox } from '../../../../../components/ups-terms-checkbox/ups-terms-checkbox';
import { useWalletRegistrationFormContext } from '../../contexts/wallet-registration-form-context';
import { FORM_REQUIREMENTS } from '../../../../utilities/constants';

export const AddFundsStep = () => {
  const {
    fundAmount,
    setFundAmount,
    submitting,
    autoFundAmount,
    setAutoFundAmount,
    autoFundEnabled,
    setMaxDailyFundings,
    maxDailyFundings,
    setBalanceThreshold,
    balanceThreshold,
    useAsDefault,
    setUseAsDefault,
    orderRates,
    updateAutoFundEnabled,
    creditCardProcessingFee,
    creditCardProcessingFeePercentage,
    totalWithFees,
    previewError,
    triggerRetry,
    isSurchargeLoading,
  } = useAddFundsContext();
  const { walletToRegister, selectedFulfillmentPlanIds, shouldShowOldTermsTab, terms, nonDefaultTerms } =
    useWalletRegistrationModalContext();
  const { walletForm, updateWalletForm } = useWalletRegistrationFormContext();
  const { providerIdRequiringPhoneVerification } = useContext(PhoneVerificationContext);
  const upsTermsAccepted = walletForm?.[FORM_REQUIREMENTS.METADATA]?.[FORM_REQUIREMENTS.UPS_TERMS] ?? false;
  const handleUpsTermsChange = useCallback(
    (checked) => {
      updateWalletForm([FORM_REQUIREMENTS.METADATA, FORM_REQUIREMENTS.UPS_TERMS], checked);
    },
    [updateWalletForm],
  );
  const { walletId, newLabelProviderIds = [] } = walletToRegister;
  const defaultSellerCurrencyCode = useSelector((state) => Global.selectSellerDefaultCurrency(state));
  const { balance } = useWalletBalance(walletId);
  const walletBalance = balance || {
    value: 0,
    code: defaultSellerCurrencyCode,
  };
  const isFromCreateLabel = selectedFulfillmentPlanIds.length > 0;
  const minimumAmount = isFromCreateLabel ? orderRates?.value : MINIMUM_PURCHASE_AMT;
  const toggleUseAsDefault = useCallback(() => setUseAsDefault((val) => !val), [setUseAsDefault]);
  const { canUseSetting: allowDefaultPurchase } = useWalletSetting(walletId, walletSettingsEnum.defaultPurchaseAmount);
  const intl = useIntl();

  const [isOtherAmountVisible, setIsOtherAmountVisible] = useState(false);

  const [areAutoFundSettingsVisible, setAreAutoFundSettingsVisible] = useState(true);

  const toggleAutoFundSettings = useCallback(() => {
    setAreAutoFundSettingsVisible((prev) => !prev);
  }, []);

  // Get carrier and service information from the fulfillment plans
  const fulfillmentPlans =
    useSelector((state) =>
      Global.selectEntitiesFulfillmentPlansByIds(state, {
        fulfillmentPlanIds: selectedFulfillmentPlanIds,
      }),
    ) ?? emptyArray;
  const firstFulfillmentPlan = fulfillmentPlans[0];
  const sellerLabelProviderId = firstFulfillmentPlan?.labelConfiguration?.sellerLabelProviderId;
  const serviceId = firstFulfillmentPlan?.labelConfiguration?.serviceId;
  const sellerLabelProviders = useSelector(Global.selectSellerLabelProviders);
  const referenceDataLabelProviders = useSelector(Global.selectReferenceDatagetProviders);
  const labelProvider = sellerLabelProviders.find((provider) => provider.sellerLabelProviderId === sellerLabelProviderId);
  const service = useSelector((state) => Global.selectSellerServiceById(state, { serviceId }));
  const providerId = labelProvider?.labelProviderId;
  const serviceName = service?.name;

  // Check if multiple carriers are being used in bulk flow
  const uniqueCarrierIds = useMemo(() => {
    const carrierIds = fulfillmentPlans
      .map((plan) => {
        const slpId = plan?.labelConfiguration?.sellerLabelProviderId;
        const sellerProvider = sellerLabelProviders.find((p) => p.sellerLabelProviderId === slpId);
        const refProvider = referenceDataLabelProviders.find((p) => p.labelProviderId === sellerProvider?.labelProviderId);
        return refProvider?.carrierId;
      })
      .filter(Boolean);
    return [...new Set(carrierIds)];
  }, [fulfillmentPlans, sellerLabelProviders, referenceDataLabelProviders]);
  const isMultiCarrier = uniqueCarrierIds.length > 1;

  const onArrowButtonPress = useCallback(
    (updatedValue) => {
      setMaxDailyFundings(updatedValue);
    },
    [setMaxDailyFundings],
  );

  const isUpsWalletEligible = newLabelProviderIds.includes(Providers.UPSWallet.labelProviderId);
  const shouldShowUpsTerms = !shouldShowOldTermsTab && isUpsWalletEligible;

  // Auto-accept regular terms and non-default terms when the old terms tab is not shown
  // (new accounts don't see the separate terms tab, so we auto-accept terms here)
  useEffect(() => {
    if (!shouldShowOldTermsTab) {
      if (terms?.length > 0) {
        updateWalletForm([FORM_REQUIREMENTS.METADATA, FORM_REQUIREMENTS.TERMS], true);
      }
      if (Object.keys(nonDefaultTerms ?? {}).length > 0) {
        updateWalletForm([FORM_REQUIREMENTS.METADATA, FORM_REQUIREMENTS.NONDEFAULT_TERMS], true);
      }
    }
  }, [shouldShowOldTermsTab, terms, nonDefaultTerms, updateWalletForm]);

  return (
    <div className={classnames(commonStyles['step-content'], styles['text-color'])}>
      <AddFundsErrorSection currencyCode={defaultSellerCurrencyCode} />
      {providerIdRequiringPhoneVerification ? (
        <PhoneVerificationBanner sellerLabelProviderId={providerIdRequiringPhoneVerification} />
      ) : null}

      <div className={styles.description}>
        <Text
          textType={TextType.Paragraph}
          value={isFromCreateLabel ? messages.shipstationBalanceDescriptionLabelFlow : messages.shipstationBalanceDescription}
        />
      </div>

      {isFromCreateLabel ? (
        <CreateLabelDisplay isMultiCarrier={isMultiCarrier} orderRates={orderRates} providerId={providerId} serviceName={serviceName} />
      ) : null}

      <div className={styles['balance-card']}>
        <FlexColumn className={styles['balance-card-content']}>
          <FlexRow className={styles['balance-header']}>
            <FlexRow className={styles['balance-title-row']}>
              <Text className={styles['balance-title']} textType={TextType.H4} value={messages.shipstationBalanceTitle} />
              <InfoIcon
                className={styles['balance-icon']}
                size='small'
                tooltipText={intl.formatMessage(messages.shipstationBalanceTooltip)}
              />
            </FlexRow>
            <Text
              className={styles['balance-amount']}
              textType={TextType.H4}
              value={<FormattedCurrencyContainer currencyCode={walletBalance.code} value={walletBalance.value} />}
            />
          </FlexRow>

          <div className={styles.divider} />

          <FlexColumn className={styles['add-funds-section']}>
            <Text textType={TextType.H4} value={messages.amountToAdd} />
            <FlexRow className={styles['add-funds-container']}>
              <div className={styles['button-container']}>
                <AddFundButtons
                  disabled={submitting}
                  fundAmount={fundAmount}
                  minimumAmount={minimumAmount}
                  onChangeAction={setFundAmount}
                  onClickAction={setFundAmount}
                  onOtherButtonVisibilityChange={setIsOtherAmountVisible}
                  sellerCurrencyCode={walletBalance.code}
                />
              </div>
            </FlexRow>
            {allowDefaultPurchase && isOtherAmountVisible ? (
              <Checkbox
                checked={useAsDefault}
                className={styles['default-checkbox']}
                disabled={submitting}
                label={<FormattedMessage {...messages.makeDefault} />}
                onChange={toggleUseAsDefault}
              />
            ) : null}
            {!!previewError && <RetrySection isLoading={isSurchargeLoading} triggerRetry={triggerRetry} />}

            <div>
              <FlexRow className={styles['auto-fund-header']}>
                <Checkbox
                  checked={autoFundEnabled}
                  className={styles['auto-fund-checkbox']}
                  label={intl.formatMessage(messages.autoFundCheckboxLabel)}
                  onChange={updateAutoFundEnabled}
                />
                <InfoIcon
                  className={styles['auto-fund-info-icon']}
                  size='small'
                  tooltipText={intl.formatMessage(messages.autoFundCheckboxLabel)}
                />
                <Button className={styles['view-settings-link']} onClick={toggleAutoFundSettings} theme={THEME_CONSTANTS.link}>
                  <FlexRow className={styles['view-settings-content']}>
                    {intl.formatMessage(messages.viewSettings)}
                    <FontAwesomeIcon icon={areAutoFundSettingsVisible ? faChevronUp : faChevronDown} />
                  </FlexRow>
                </Button>
              </FlexRow>

              {areAutoFundSettingsVisible ? (
                <FlexRow className={styles['auto-fund-inputs']}>
                  <FlexColumn className={styles['input-group']}>
                    <Text className={styles['input-label']} textType={TextType.Label} value={messages.autoFundAmount} />
                    <CurrencyInput
                      className={styles['currency-input']}
                      currencyCode={walletBalance.code}
                      disabled={submitting}
                      onChange={setAutoFundAmount}
                      value={autoFundAmount}
                    />
                  </FlexColumn>

                  <FlexColumn className={styles['input-group']}>
                    <Text className={styles['input-label']} textType={TextType.Label} value={messages.addIfBelow} />
                    <CurrencyInput
                      className={styles['currency-input']}
                      currencyCode={walletBalance.code}
                      disabled={submitting}
                      onChange={setBalanceThreshold}
                      value={balanceThreshold}
                    />
                  </FlexColumn>
                  <FlexColumn className={styles['input-group']}>
                    <Text className={styles['input-label']} textType={TextType.Label} value={messages.maxFunding} />
                    <FlexColumn className={styles['number-input-wrapper']}>
                      <NumberInput
                        className={styles['number-input']}
                        disabled={submitting}
                        min={1}
                        onArrowButtonPress={onArrowButtonPress}
                        onChange={setMaxDailyFundings}
                        value={maxDailyFundings}
                      />
                      <Text className={styles['times-per-day-text']} textType={TextType.Caption} value={messages.timesPerDay} />
                    </FlexColumn>
                  </FlexColumn>
                </FlexRow>
              ) : null}
            </div>
          </FlexColumn>
        </FlexColumn>
      </div>
      {shouldShowUpsTerms ? <UpsTermsCheckbox checked={upsTermsAccepted} onChange={handleUpsTermsChange} /> : null}
      <div className={styles['insurance-section-container']}>
        <ApplyInsuranceCheckbox />
      </div>
      {!previewError && creditCardProcessingFee > 0 && (
        <div className={styles['funds-summary-container']}>
          <FundsSummary
            baseAmount={fundAmount}
            className={styles['funds-summary']}
            creditCardProcessingFee={creditCardProcessingFee}
            creditCardProcessingFeePercentage={creditCardProcessingFeePercentage}
            currencyCode={defaultSellerCurrencyCode}
            totalWithFees={totalWithFees}
          />
        </div>
      )}
    </div>
  );
};
